import { useMemo, useState } from "react";

const industries = [
  "Government",
  "BFSI (Banking & Finance)",
  "Defence & Critical Infrastructure",
  "Digital-Native Enterprises",
  "Other",
];

const inquiryTypes = ["Assessment", "Demo", "Managed Services", "Partnership", "General"];

function FieldLabel({ htmlFor, children }) {
  return (
    <label
      htmlFor={htmlFor}
      className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]"
    >
      {children}
    </label>
  );
}

export default function Contact() {
  const [status, setStatus] = useState("idle");

  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    role: "",
    industry: industries[0],
    inquiryType: inquiryTypes[0],
    message: "",
  });

  const canSubmit = useMemo(() => {
    return form.name.trim() && form.email.trim() && form.organization.trim() && form.message.trim();
  }, [form.email, form.message, form.name, form.organization]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-[var(--gradient-hero)] p-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_25%,rgba(37,99,235,0.18),transparent_45%),radial-gradient(circle_at_75%_35%,rgba(6,182,212,0.10),transparent_55%)]" />
        <div className="relative">
          <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
            Contact / Assessments
          </div>
          <h1
            className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Start Your Identity Security Journey
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-[var(--text-secondary)] sm:text-base">
            Let's discuss how to build trust-first digital operations.
          </p>
        </div>
      </div>

      <div className="mt-14 grid gap-10 lg:grid-cols-2 lg:items-start">
        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur-xl sm:p-10">
          <h2
            className="text-xl font-semibold tracking-tight"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Request an assessment
          </h2>

          {status === "submitted" ? (
            <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] p-6">
              <div className="text-sm font-semibold text-[var(--text-primary)]">Submission received</div>
              <div className="mt-2 text-sm leading-6 text-[var(--text-secondary)]">
                This is a demo form (no backend). If you want, I can wire this to email or a CRM endpoint next.
              </div>
            </div>
          ) : null}

          <form
            className="mt-6 grid gap-5"
            onSubmit={(e) => {
              e.preventDefault();
              if (!canSubmit) return;
              setStatus("submitted");
            }}
          >
            <div className="grid gap-2">
              <FieldLabel htmlFor="name">Name</FieldLabel>
              <input
                id="name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="h-11 rounded-xl border border-white/10 bg-white/[0.02] px-4 text-sm text-[var(--text-primary)] outline-none ring-0 placeholder:text-white/30 focus:border-[var(--accent-trust)]"
                placeholder="Your full name"
                autoComplete="name"
                required
              />
            </div>

            <div className="grid gap-2">
              <FieldLabel htmlFor="email">Email</FieldLabel>
              <input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className="h-11 rounded-xl border border-white/10 bg-white/[0.02] px-4 text-sm text-[var(--text-primary)] outline-none placeholder:text-white/30 focus:border-[var(--accent-trust)]"
                placeholder="name@company.com"
                autoComplete="email"
                required
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-2">
                <FieldLabel htmlFor="organization">Organization</FieldLabel>
                <input
                  id="organization"
                  value={form.organization}
                  onChange={(e) => setForm((f) => ({ ...f, organization: e.target.value }))}
                  className="h-11 rounded-xl border border-white/10 bg-white/[0.02] px-4 text-sm text-[var(--text-primary)] outline-none placeholder:text-white/30 focus:border-[var(--accent-trust)]"
                  placeholder="Company / Agency"
                  required
                />
              </div>
              <div className="grid gap-2">
                <FieldLabel htmlFor="role">Role/Title</FieldLabel>
                <input
                  id="role"
                  value={form.role}
                  onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                  className="h-11 rounded-xl border border-white/10 bg-white/[0.02] px-4 text-sm text-[var(--text-primary)] outline-none placeholder:text-white/30 focus:border-[var(--accent-trust)]"
                  placeholder="CISO / CIO / IAM Lead"
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-2">
                <FieldLabel htmlFor="industry">Industry</FieldLabel>
                <select
                  id="industry"
                  value={form.industry}
                  onChange={(e) => setForm((f) => ({ ...f, industry: e.target.value }))}
                  className="h-11 rounded-xl border border-white/10 bg-white/[0.02] px-4 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent-trust)]"
                >
                  {industries.map((i) => (
                    <option key={i} value={i}>
                      {i}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid gap-2">
                <FieldLabel htmlFor="inquiry">Inquiry type</FieldLabel>
                <select
                  id="inquiry"
                  value={form.inquiryType}
                  onChange={(e) => setForm((f) => ({ ...f, inquiryType: e.target.value }))}
                  className="h-11 rounded-xl border border-white/10 bg-white/[0.02] px-4 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent-trust)]"
                >
                  {inquiryTypes.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid gap-2">
              <FieldLabel htmlFor="message">Message</FieldLabel>
              <textarea
                id="message"
                value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                className="min-h-[140px] rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 text-sm text-[var(--text-primary)] outline-none placeholder:text-white/30 focus:border-[var(--accent-trust)]"
                placeholder="Tell us about your identity challenges, environment (cloud/on-prem/hybrid), and desired outcomes."
                required
              />
            </div>

            <button
              type="submit"
              disabled={!canSubmit}
              className="inline-flex h-11 items-center justify-center rounded-xl bg-[var(--accent-trust)] px-5 text-sm font-semibold text-white shadow-lg shadow-blue-500/20 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Submit
            </button>
          </form>
        </div>

        <div className="grid gap-6">
          <div className="rounded-3xl border border-white/10 bg-[var(--gradient-card)] p-8 backdrop-blur-xl">
            <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
              Quick contact
            </div>
            <div className="mt-4 grid gap-2 text-sm">
              <div className="text-[var(--text-secondary)]">
                Email: <span className="text-[var(--text-primary)]">info@ivalue.co.in</span>
              </div>
              <div className="text-[var(--text-secondary)]">
                Phone: <span className="text-[var(--text-primary)]">+91 95359 99473</span>
              </div>
              <div className="text-[var(--text-secondary)]">
                Office locations: <span className="text-[var(--text-primary)]">iValue Group (India-HQ) No. 903/1/1, 19th Main Road, 4th Sector, H.S.R. Layout, Bangalore – 560102</span>
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-8">
            <h3
              className="text-lg font-semibold tracking-tight"
              style={{ fontFamily: "var(--font-display)" }}
            >
              What to expect
            </h3>
            <div className="mt-4 grid gap-3 text-sm text-[var(--text-secondary)]">
              {[
                ["Discovery Call (30 min)", "Understand your identity challenges"],
                ["Assessment Proposal", "Tailored scope & approach"],
                ["Assessment Execution (2–4 weeks)", "Analysis across identity, keys, and trust controls"],
                ["Findings & Roadmap", "Executive presentation with prioritized roadmap"],
              ].map(([t, d]) => (
                <div key={t} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                  <div className="text-sm font-semibold text-[var(--text-primary)]">{t}</div>
                  <div className="mt-2 text-sm leading-6 text-[var(--text-secondary)]">{d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
